import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  LinearProgress, MenuItem, Snackbar, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import { DeleteOutline as DeleteIcon, PlayArrow as RunIcon } from '@mui/icons-material';
import { createFuzzCampaign, deleteFuzzCampaign, listFuzzCampaigns, listFuzzTemplates } from 'api/features';
import { apiPost } from 'api/service';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import { GREEN, MONO } from './scanUi';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;
const STATUS = { running: ['#60a5fa', 'Running'], completed: [GREEN, 'Finished'], failed: ['#ef4444', 'Failed'], draft: ['#94a3b8', 'Waiting'], queued: ['#94a3b8', 'Waiting'] };
const BLANK = { name: '', fuzz_type: 'sqli', target_url: '', method: 'GET', param_name: 'q', custom_payloads: '', timeout: 30, max_payloads: 120 };
const fail = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const firstError = (e) => {
  const d = e?.response?.data;
  const v = d && typeof d === 'object' ? Object.values(d)[0] : null;
  return Array.isArray(v) ? v[0] : (typeof v === 'string' ? v : null);
};

export default function FuzzingPage() {
  const navigate = useNavigate();
  const { currentTargetId, targets } = useFuzzScanTarget();
  const userTarget = useMemo(() => (targets || []).find((t) => String(t.id) === String(currentTargetId)) || null, [targets, currentTargetId]);
  const [rows, setRows] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const say = (severity, message) => setToast({ open: true, severity, message });

  const load = useCallback(async () => {
    if (!currentTargetId) { setRows([]); return; }
    try {
      const d = await listFuzzCampaigns({ user_target: currentTargetId });
      setRows(Array.isArray(d) ? d : d.results || []);
    } catch (e) { say('error', fail(e, 'Could not load the campaigns.')); setRows([]); }
  }, [currentTargetId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listFuzzTemplates({ preview_size: 3 }).then((d) => setTemplates(Array.isArray(d) ? d : d.results || [])).catch(() => setTemplates([]));
  }, []);
  // A running campaign moves on its own: look again every few seconds while one is active.
  useEffect(() => {
    if (!(rows || []).some((c) => ['running', 'draft', 'queued'].includes(c.status))) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [rows, load]);

  const pick = (t) => { setForm({ ...BLANK, fuzz_type: t.id, param_name: (t.recommended_params || ['q'])[0] || 'q', max_payloads: Math.min(Number(t.payload_count) || 120, 1000) }); setOpen(true); };
  const label = (id) => templates.find((t) => t.id === id)?.label || String(id || '').toUpperCase();

  const create = async () => {
    setSaving(true);
    try {
      const custom = form.custom_payloads.split('\n').map((l) => l.trim()).filter(Boolean);
      await createFuzzCampaign({
        user_target: currentTargetId,
        name: form.name || `${label(form.fuzz_type)} test`,
        template: form.fuzz_type,
        options: { fuzz_type: form.fuzz_type, target_url: form.target_url.trim(), method: form.method, param_name: form.param_name.trim(), custom_payloads: custom, timeout: Number(form.timeout) || 30, max_payloads: Number(form.max_payloads) || 120 }
      });
      setOpen(false); setForm(BLANK); say('success', 'Test started.'); await load();
    } catch (e) { say('error', firstError(e) || fail(e, 'Could not start the test.')); } finally { setSaving(false); }
  };
  const remove = async (c) => { try { await deleteFuzzCampaign(c.id); say('success', 'Deleted.'); await load(); } catch (e) { say('error', fail(e, 'Could not delete it.')); } };
  const rerun = async (c) => { try { await apiPost(`/api/fuzz-campaigns/${c.id}/start/`, {}); say('success', 'Test started again.'); await load(); } catch (e) { say('error', fail(e, 'Could not start it.')); } };

  if (rows === null) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  const found = rows.reduce((n, c) => n + (c.findings_found || 0), 0);
  const field = (k) => ({ value: form[k], onChange: (e) => setForm((p) => ({ ...p, [k]: e.target.value })) });

  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Fuzzing</Typography>
          <Typography color="text.secondary" variant="body2">
            Sends many unusual inputs to one page of your website to see if it breaks. {userTarget ? <>Testing <b style={{ fontFamily: MONO }}>{userTarget.target?.value}</b>.</> : 'Choose a website first.'}
          </Typography>
        </Box>
        <Button variant="contained" disabled={!userTarget} onClick={() => { setForm({ ...BLANK, target_url: userTarget?.target?.value?.includes('://') ? userTarget.target.value : `https://${userTarget?.target?.value || ''}` }); setOpen(true); }} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: GREEN } }}>New test</Button>
      </Stack>
      <Alert severity="info">Only test websites you own or have permission to test. Tests can only be aimed at the website you have chosen and its subdomains.</Alert>
      {!userTarget && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => navigate('/targets')}>Add a website</Button>}>No website is selected.</Alert>}

      {templates.length > 0 && (
        <Box>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Start from a kind of test</Typography>
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            {templates.map((t) => (
              <Box key={t.id} component="button" type="button" disabled={!userTarget} onClick={() => { pick(t); setForm((p) => ({ ...p, target_url: userTarget?.target?.value?.includes('://') ? userTarget.target.value : `https://${userTarget?.target?.value || ''}` })); }}
                sx={{ ...panel, p: 2, flex: '1 1 220px', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', '&:hover:not(:disabled)': { borderColor: GREEN }, '&:disabled': { opacity: 0.5, cursor: 'default' } }}>
                <Typography sx={{ fontWeight: 700 }}>{t.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>{t.description || `${t.payload_count} inputs`}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>{t.payload_count} inputs</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Box sx={{ ...panel, overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" sx={{ p: 2.2 }}>
          <Typography sx={{ fontWeight: 700 }}>Your tests</Typography>
          <Typography variant="caption" color="text.secondary">{rows.length} test{rows.length === 1 ? '' : 's'} · {found} problem{found === 1 ? '' : 's'} found</Typography>
        </Stack>
        {!rows.length && <Typography color="text.secondary" variant="body2" sx={{ px: 2.8, py: 2.4, borderTop: 1, borderColor: 'divider' }}>{userTarget ? 'No tests yet. Start one above.' : 'Choose a website to see its tests.'}</Typography>}
        {rows.map((c) => {
          const [color, text] = STATUS[c.status] || STATUS.draft;
          const o = c.options || {};
          const pct = c.total_inputs ? Math.min(100, Math.round(((c.processed_inputs || 0) / c.total_inputs) * 100)) : 0;
          return (
            <Stack key={c.id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ flex: '2 1 260px', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>{c.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO, wordBreak: 'break-all' }}>{label(c.template)} · {o.method || 'GET'} {o.target_url || ''}</Typography>
              </Box>
              <Pill color={color}>{text.toUpperCase()}</Pill>
              <Box sx={{ flex: '1 1 130px' }}>
                <LinearProgress variant="determinate" value={c.status === 'completed' ? 100 : pct} sx={{ height: 6, borderRadius: 3, bgcolor: alpha(color, 0.16), '& .MuiLinearProgress-bar': { bgcolor: color } }} />
                <Typography variant="caption" color="text.secondary">{c.processed_inputs || 0} of {c.total_inputs || 0} inputs</Typography>
              </Box>
              {c.findings_found > 0 ? <Pill color="#ef4444">{c.findings_found} FOUND</Pill> : <Typography variant="caption" color="text.secondary" sx={{ width: 64 }}>none found</Typography>}
              <Button size="small" onClick={() => setDetail(c)} sx={{ textTransform: 'none', color: GREEN, fontWeight: 700 }}>Details</Button>
              {c.status !== 'running' && <Tooltip title="Run again"><IconButton size="small" aria-label="Run again" onClick={() => rerun(c)}><RunIcon fontSize="small" /></IconButton></Tooltip>}
              <Tooltip title="Delete"><IconButton size="small" aria-label="Delete" onClick={() => remove(c)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </Stack>
          );
        })}
      </Box>

      <Dialog open={open} onClose={() => (saving ? null : setOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>New fuzz test</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" label="Name" placeholder="Search box injection" {...field('name')} />
            <TextField select size="small" label="Kind of test" {...field('fuzz_type')}>
              {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Page to test" placeholder="https://shop.example.com/search" helperText="Must be on your website or one of its subdomains" {...field('target_url')} />
            <Stack direction="row" gap={1.5}>
              <TextField select size="small" label="Method" sx={{ minWidth: 110 }} {...field('method')}>{['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}</TextField>
              <TextField fullWidth size="small" label="Field to fill" placeholder="q" {...field('param_name')} />
            </Stack>
            <Stack direction="row" gap={1.5}>
              <TextField size="small" type="number" label="Wait up to (seconds)" helperText="Most 60" {...field('timeout')} />
              <TextField size="small" type="number" label="Inputs to try" helperText="Most 1000" {...field('max_payloads')} />
            </Stack>
            {form.fuzz_type === 'custom' && <TextField multiline minRows={4} size="small" label="Your own inputs, one per line" {...field('custom_payloads')} />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={saving || !form.target_url.trim() || !form.param_name.trim()} variant="contained" onClick={create} sx={{ bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: GREEN } }}>{saving ? 'Starting…' : 'Start test'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detail?.name}</DialogTitle>
        <DialogContent>
          {detail && (
            <Stack spacing={1.2} sx={{ mt: 0.5 }}>
              <Typography variant="body2"><b>Kind:</b> {label(detail.template)}</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}><b>Page:</b> <span style={{ fontFamily: MONO }}>{detail.options?.method || 'GET'} {detail.options?.target_url}</span></Typography>
              <Typography variant="body2"><b>Field:</b> {detail.options?.param_name}</Typography>
              <Typography variant="body2"><b>Inputs tried:</b> {detail.processed_inputs || 0} of {detail.total_inputs || 0}</Typography>
              <Typography variant="body2"><b>Problems found:</b> {detail.findings_found || 0}</Typography>
              {detail.error_message && <Alert severity="error">{detail.error_message}</Alert>}
              {detail.findings_found > 0 && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => navigate('/vulns')}>Open findings</Button>}>What was found is listed in Findings, with the input that caused it.</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
      </Dialog>
      <Snackbar open={toast.open} autoHideDuration={4500} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Stack>
  );
}
